import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { Credential } from '@/types';
import { useMutation } from '@/utils/api';

type CredentialModalState =
  | { kind: 'none' }
  | { kind: 'list' }
  | { kind: 'add' };

interface UseCredentialManagerOptions {
  assetKey: string;
  /** The asset's `secret` field — the credentialId of the default credential */
  assetSecret?: string;
  /** Whether this context supports set-as-default (assets yes, seeds no) */
  supportsDefault?: boolean;
  /** Asset group/identifier — locate the recorder-agent job for status/reopen. */
  group?: string;
  identifier?: string;
}

export function useCredentialManager({
  assetKey,
  assetSecret,
  supportsDefault = false,
  group,
  identifier,
}: UseCredentialManagerOptions) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  // F14 fix: modal state resets naturally when the assetKey changes because
  // the parent only passes a non-empty assetKey when a single web app is
  // selected. When the selection changes, `open()` is gated behind a new
  // selection check, and `onClose()` returns to 'none'. The hook is keyed
  // on assetKey for its credential data — stale modals can't outlive the
  // selection.
  const [modalState, setModalState] = useState<CredentialModalState>({
    kind: 'none',
  });

  const {
    data: credentials = [],
    status: credentialsStatus,
    invalidate: invalidateCredentials,
  } = useMy(
    {
      resource: 'credential',
      query: {
        node: {
          labels: ['Credential'],
          filters: [{ field: 'accountKey', operator: '=', value: assetKey }],
        },
      },
    },
    // F3 fix: don't fire API call with empty assetKey
    { enabled: !!assetKey }
  );

  const { mutateAsync: deleteCredentialMutation } = useMutation({
    mutationFn: async ({
      credentialId,
      credentialType,
    }: {
      credentialId: string;
      credentialType: string;
    }) => {
      const response = axios.delete('/broker', {
        data: {
          CredentialID: credentialId,
          ResourceKey: assetKey,
          Type: credentialType || 'burp-authentication',
        },
      });

      toast.promise(response, {
        loading: 'Deleting credential...',
        success: 'Credential deleted successfully',
        error: 'Failed to delete credential',
      });

      return (await response).data;
    },
    defaultErrorMessage: 'Failed to delete credential',
  });

  // C2 fix: set-default mutation — mirrors AuthenticationTab pattern
  const { mutateAsync: setDefaultCredentialMutation } = useMutation({
    mutationFn: async (credentialId: string) => {
      const response = axios.put('/asset', {
        key: assetKey,
        secret: credentialId,
      });

      toast.promise(response, {
        loading: 'Setting default credential...',
        success: 'Default credential updated',
        error: 'Failed to set default credential',
      });

      return (await response).data;
    },
    defaultErrorMessage: 'Failed to set default credential',
  });

  // Derive default credential ID from the asset's secret field
  const defaultCredentialId = assetSecret;

  // F4 fix: check credentialsStatus to avoid showing wrong modal during loading
  // C9 fix: handle error status — show list view so error state is visible
  const open = () => {
    if (credentialsStatus === 'error') {
      // Show list view so empty/error state is visible, not the add form
      setModalState({ kind: 'list' });
      return;
    }
    if (credentialsStatus === 'pending') {
      // Data still loading — default to list view (will populate when data arrives)
      setModalState({ kind: 'list' });
      return;
    }
    if (credentials.length === 0) {
      setModalState({ kind: 'add' });
    } else {
      setModalState({ kind: 'list' });
    }
  };

  const onAddClick = () => {
    setModalState({ kind: 'add' });
  };

  const onBackToList = () => {
    setModalState({ kind: 'list' });
  };

  const onClose = () => {
    setModalState({ kind: 'none' });
  };

  // F5 fix: removed auto-close logic (stale closure issue).
  // User closes manually after deleting the last credential.
  const handleDelete = async (credential: Credential) => {
    // N2 fix: defense-in-depth — refuse to fire with empty assetKey (seeds)
    if (!assetKey) return;
    try {
      await deleteCredentialMutation({
        credentialId: credential.credentialId,
        credentialType: credential.type,
      });
      invalidateCredentials();
    } catch {
      // toast already shown by mutation
    }
  };

  // C2 fix: set-default handler — mirrors AuthenticationTab handleSetDefault
  const handleSetDefault = async (credential: Credential) => {
    // N2 fix: defense-in-depth — refuse to fire with empty assetKey (seeds)
    if (!assetKey) return;
    try {
      await setDefaultCredentialMutation(credential.credentialId);
      invalidateCredentials();
      // C1 fix: also invalidate asset queries so asset.secret refreshes
      // (defaultCredentialId derives from assetSecret which comes from the
      // parent's asset query — without this the "Default" badge stays stale)
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
        refetchType: 'active',
      });
    } catch {
      // toast already shown by mutation
    }
  };

  return {
    modalState,
    credentials: credentials as Credential[],
    credentialsStatus,
    defaultCredentialId,
    supportsDefault,
    open,
    onAddClick,
    onBackToList,
    onClose,
    handleDelete,
    handleSetDefault,
    invalidateCredentials,
    assetKey,
    group,
    identifier,
  };
}

export type CredentialManagerReturn = ReturnType<typeof useCredentialManager>;
